import {HomeScreens, ShopperScreens} from '../../../../home/navigation/types';
import React, {Fragment, useCallback, useMemo, useState} from 'react';
import {
  useAddItemRemark,
  useConfig,
  useDropOrderItem,
  useIsEligibleForAdjacentOOS,
  useUPCOrder,
} from '../../../../itemDetails/hooks/ItemDetails';
import {
  useColorThemeType,
  useFontTheme,
} from '../../../../appStyles/hooks/theme';

import AppModal from '../../../../common/components/CustomModal/AppModal';
import {Box} from 'native-base';
import DropItemOOS from '../DropItemOOS/DropItemOOS';
import Dropdown from '../../../../common/components/Dropdown/Dropdown';
import {H3Text} from '../../../../components/core/text';
import {Modules} from '@buncha/utils/types';
import {StyleSheet} from 'react-native';
import {SubstitutionChoice} from '@buncha/itemDetails/types/itemDetails';
import font from '../../../../appStyles/style/font';
import {getWidth} from '../../../../utils/common';
import listener from '../../../../config/listener';
import {navigation} from '../../../../config/navigation';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';

interface ModalBodyProps {
  onClose: any;
  orderItemId: number;
  disableNavigation?: boolean;
  disableState?: boolean;
  open: boolean;
  hasListnerReq?: boolean;
  isUPCMode?: boolean;
  isPermanentlyDropped?: boolean;
  isAdjacentOOSItem?: boolean;
  substitutionChoice?: string;
  refreshBuyers?: () => Promise<void>;
  catalogItemId: number | undefined;
  orderId: number;
  onCloseAOOSModal?: () => void;
}

interface RouteParamsType {
  runId?: number;
}

const DropItemModalBody = (props: ModalBodyProps) => {
  const route = useRoute();
  const routeParams: RouteParamsType | undefined = route?.params;
  const runId = routeParams?.runId || 0;
  const theme = useColorThemeType('main');
  const {reasonList} = useConfig();
  const choiceConfirmed = false;
  const [selectedReason, setSelectedReason] = useState('');
  const {dropOrderItem, droppingItem} = useDropOrderItem();
  const {addItemRemark, loading: remarkLoading} = useAddItemRemark();
  const {checkAdjacentOOS, checkingAdjacetStores, adjacentOOSData} =
    useIsEligibleForAdjacentOOS();
  const {dropUPCItem, droppingItem: UPCDropping} = useUPCOrder();
  const [showOOSDropModal, setShowOOSDropModal] = useState(false);
  const {upcOrderItem} = useAppSelector(gState => gState.itemScanner);
  const fontTheme = useFontTheme('mortise');
  const {title, button} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const setSelectedReasonHandler = useCallback(
    (reason: string) => {
      setSelectedReason(reason);
    },
    [setSelectedReason],
  );

  const closeModal = useCallback(() => {
    setShowOOSDropModal(s => !s);
    navigation.goBack();
    props?.onCloseAOOSModal?.();
  }, [props]);

  const dropItemAndMarkReason = useCallback(async () => {
    if (props?.isUPCMode) {
      const res = await dropUPCItem(upcOrderItem?.id ?? 0);
      if (res) navigation.goBack();
    } else {
      const res = await checkAdjacentOOS({
        runId: runId,
        orderId: props?.orderId,
        orderItemId: props?.orderItemId,
        catalogItemId: props?.catalogItemId || 0,
      });
      if (res?.isEligible) {
        setShowOOSDropModal(true);
      } else {
        const storeTime =
          props?.substitutionChoice !== SubstitutionChoice.noSubstitution;
        await dropOrderItem(
          runId,
          [props?.orderItemId],
          props?.isAdjacentOOSItem,
          storeTime,
        );
        await addItemRemark(props?.orderItemId, selectedReason);
        if (props?.refreshBuyers) props.refreshBuyers();
        if (!props.disableNavigation)
          navigation.navigate(HomeScreens.Shopper, {
            screen: ShopperScreens.BatchDetailsPage,
            params: {
              runId: runId,
            },
          });
      }
    }
    props?.onClose();
    if (props?.hasListnerReq) listener.emit('orderPackDropRefresh');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, selectedReason]);

  const loading =
    droppingItem || UPCDropping || remarkLoading || checkingAdjacetStores;

  const getHeader = useMemo(() => {
    return choiceConfirmed ? title?.selectReason : title?.dropItem;
  }, [choiceConfirmed, title?.dropItem, title?.selectReason]);

  const getBody = useMemo(() => {
    return (
      <Box>
        <H3Text style={[styles.childTypo]}>
          {choiceConfirmed
            ? title?.selectReasonToDrop
            : title?.removeItemConfirm}
        </H3Text>
        {choiceConfirmed && (
          <Box style={styles.dropDown}>
            <Dropdown
              data={reasonList}
              key="reason"
              value="reason"
              onChange={(val: any) => setSelectedReasonHandler(val)}
              selectedValue={selectedReason}
            />
          </Box>
        )}
      </Box>
    );
  }, [
    choiceConfirmed,
    reasonList,
    selectedReason,
    setSelectedReasonHandler,
    title?.removeItemConfirm,
    title?.selectReasonToDrop,
  ]);

  return (
    <Fragment>
      <AppModal
        isOpen={props?.open}
        onClose={() => props?.onClose()}
        topCTAHandelr={dropItemAndMarkReason}
        bottomCTAHandelr={props.onClose}
        hideBottomCta={choiceConfirmed ? true : false}
        title={getHeader}
        modalBody={getBody}
        ctaBottomTitle={button?.cancel}
        titleStyle={{fontFamily: fontTheme[700].normal, ...styles.titleTypo}}
        ctaTopTitle={choiceConfirmed ? button?.ok : button?.confimDrop}
        topCTAColor={
          choiceConfirmed
            ? theme?.background.green[100]
            : theme?.background.red[100]
        }
        loading={loading}
      />
      <DropItemOOS
        onClose={closeModal}
        open={showOOSDropModal}
        runId={runId}
        closeOnlyModal={closeModal}
        adjacentOOSData={adjacentOOSData}
      />
    </Fragment>
  );
};

export default React.memo(DropItemModalBody);

const styles = StyleSheet.create({
  parentContainer: {
    padding: 20,
  },
  headerTypo: {
    fontFamily: font.fonts['Mortise-X-Bold'],
    ...font.FontSize.fontH2,
  },
  titleTypo: {
    ...font.FontSize.fontH2,
  },
  childContainer: {
    marginTop: 10,
  },
  childTypo: {
    ...font.FontSize.fontH6,
  },
  buttonContainer: {
    top: 15,
  },
  buttonStyle: {
    width: getWidth('100%'),
  },
  cancelButton: {
    top: -15,
    borderWidth: 1,
    width: getWidth('100%'),
  },
  dropDown: {marginTop: 15},
});
